import type { WorkbookDefinition, WorkbookSource } from "@erp/shared-types";
import { cyrb53, stableSerialize } from "@erp/shared-utils";
import { workbookDependencies, workbookExecutionOrder } from "./executionOrder";
import { sheetKind } from "./sheetKinds";

/**
 * cyrb53 of a string, in base 36: the same in browser and Node. A collision
 * can only mislabel a Stale Result as Current.
 */
export function hashString(value: string): string {
  return cyrb53(value).toString(36);
}

function dataAssetInput(
  source: Extract<WorkbookSource, { kind: "data-asset" }>,
) {
  return [source.kind, source.dataAssetId, source.versionId ?? null] as const;
}

function entityInput(source: Extract<WorkbookSource, { kind: "entity" }>) {
  return [
    source.kind,
    source.entity,
    (source.filters ?? []).map((filter) => [
      filter.column,
      filter.operator,
      filter.value,
    ]),
  ] as const;
}

/**
 * A group's members resolve at read time ("all" reads whatever links the schema
 * has then, and its Data version is null), so what it reads is fixed by its
 * schema, mode and exclusions rather than by a resolved list.
 */
function schemaGroupInput(
  source: Extract<WorkbookSource, { kind: "schema-group" }>,
) {
  const { members } = source;
  return [
    source.kind,
    source.schemaId,
    members.mode === "all"
      ? (["all", [...(members.exclude ?? [])].sort()] as const)
      : (["pick", members.linkIds] as const),
    Object.entries(source.labels ?? {}).sort(([a], [b]) => a.localeCompare(b)),
    source.labelColumn ?? null,
  ] as const;
}

function forecastInput(
  source: Extract<WorkbookSource, { kind: "forecast-snapshots" }>,
) {
  return [
    source.kind,
    source.runId ?? null,
    source.asOfFrom ?? null,
    source.asOfThrough ?? null,
  ] as const;
}

function remoteSourceInput(
  source: Exclude<WorkbookSource, { kind: "file" | "input" }>,
) {
  switch (source.kind) {
    case "forecast-snapshots":
      return forecastInput(source);
    case "data-source":
      return [source.kind, source.linkId] as const;
    case "schema-group":
      return schemaGroupInput(source);
    case "mapping":
      return [source.kind, source.mappingId] as const;
    case "stateful-table":
      return [source.kind, source.tableId] as const;
    case "data-asset":
      return dataAssetInput(source);
    case "object":
      return [source.kind, source.query] as const;
    case "entity":
      return entityInput(source);
  }
}

/** What a Source reads; its name and folder are presentational. */
function sourceInput(source: WorkbookSource) {
  switch (source.kind) {
    case "file":
      return [
        source.kind,
        source.fileId,
        source.sheetName,
        source.headerRow,
      ] as const;
    case "input":
      return [source.kind, source.table.columns, source.table.rows] as const;
    default:
      return remoteSourceInput(source);
  }
}

// Sources are immutable values that keep their identity across unrelated edits, so an
// inline table is hashed once, not on every keystroke.
const sourceFingerprints = new WeakMap<WorkbookSource, string>();
/**
 * A hash of what a Source reads. A Run records it for each Source its Snapshot holds,
 * with the Data version it read folded in, so two Runs' Fingerprints match only when
 * they read the same Definition of the Source and the same data. Without a Data
 * version, it hashes the Definition alone, as Runs recorded before Data versions did.
 */
export function sourceFingerprint(
  source: WorkbookSource,
  dataVersion?: string | null,
): string {
  let definitional = sourceFingerprints.get(source);
  if (!definitional) {
    definitional = hashString(stableSerialize(sourceInput(source)));
    sourceFingerprints.set(source, definitional);
  }
  return dataVersion === undefined
    ? definitional
    : hashString(stableSerialize([definitional, dataVersion]));
}

/**
 * Each runnable Worksheet's Fingerprint: a hash of its Compute-relevant fields and the
 * Fingerprints of everything it reads, so an edit changes that Worksheet and everything
 * downstream of it, and nothing else. Blocked Worksheets (missing input, cycles) have none.
 * Server and client call this one function, so a Result's Fingerprint compares with the editor's.
 */
export function sheetFingerprints(
  definition: WorkbookDefinition,
): ReadonlyMap<string, string> {
  const upstream = new Map(
    definition.sources.map((source) => [source.id, sourceFingerprint(source)]),
  );
  const sheets = new Map(definition.sheets.map((sheet) => [sheet.id, sheet]));
  const fingerprints = new Map<string, string>();
  const dependencies = workbookDependencies(definition);
  for (const item of workbookExecutionOrder(definition)) {
    const sheet = item.kind === "sheet" ? sheets.get(item.id) : undefined;
    if (!sheet || item.blocked) continue;
    const reads = (dependencies.get(sheet.id) ?? []).map((id) => [
      id,
      upstream.get(id),
    ]);
    const fingerprint = hashString(
      stableSerialize([
        sheet.id,
        // Name, folder and number formats are presentational.
        ...sheetKind(sheet).fingerprintInput(),
        reads,
      ]),
    );
    upstream.set(sheet.id, fingerprint);
    fingerprints.set(sheet.id, fingerprint);
  }
  return fingerprints;
}
